/**
 * OverdraftAlertBanner
 * Shows a warning when projected end-of-month balance is negative.
 */

import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';

interface Props {
  projectedBalance: number;
  onDismiss: () => void;
  onViewDetails: () => void;
}

export default function OverdraftAlertBanner({ projectedBalance, onDismiss, onViewDetails }: Props) {
  if (projectedBalance >= 0) return null;

  return (
    <View style={styles.banner}>
      <TouchableOpacity style={styles.dismissButton} onPress={onDismiss} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
        <Icon name="close" size={16} color="#EF4444" />
      </TouchableOpacity>

      <View style={styles.content}>
        <Icon name="alert-circle" size={20} color="#EF4444" style={styles.icon} />
        <View style={styles.textContainer}>
          <Text style={styles.title}>Risque de découvert</Text>
          <Text style={styles.description}>
            Solde projeté en fin de mois : {projectedBalance.toLocaleString('fr-FR')} €
          </Text>
        </View>
      </View>

      <TouchableOpacity style={styles.detailsButton} onPress={onViewDetails}>
        <Text style={styles.detailsButtonText}>Voir les détails</Text>
        <Icon name="chevron-right" size={16} color="#EF4444" />
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  banner: {
    backgroundColor: 'rgba(239, 68, 68, 0.08)',
    borderRadius: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#EF4444',
    padding: spacing.md,
    marginBottom: spacing.md,
  },
  dismissButton: {
    position: 'absolute',
    top: spacing.sm,
    right: spacing.sm,
    zIndex: 1,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: spacing.sm,
    paddingRight: spacing.lg,
  },
  icon: {
    marginRight: spacing.sm,
    marginTop: 2,
  },
  textContainer: {
    flex: 1,
  },
  title: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: '#EF4444',
    marginBottom: 2,
  },
  description: {
    fontSize: typography.fontSize.sm,
    color: colors.text,
    lineHeight: 20,
  },
  detailsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  detailsButtonText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    color: '#EF4444',
  },
});
